import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch } from '@/store';
import { createDocument, deleteDocument, removeRequiredDocument, updateDocument } from '../../feature';

// Modules
import { StyledSettingsCard } from '@/modules/SettingsCommon';

// Components
import { ConfirmationModal, showSuccessToast, useForm } from '@/components';
import { SaveDocumentIcon, TrashIcon } from '@/icons';
import { Button } from '@/ui';
import { RequiredDocumentForm } from '../../components';
import { StyledWrapper } from './RequiredDocumentContainerStyles';

// Models
import { IDocument } from '@/models/documentsSettings.model';
import { PropertyType } from '@/models/property.model';

// Helpers
import { useBoolean, useModal } from '@/hooks';
import { TRequiredDocumentFormValues } from '../../helpers';

type TRequiredDocumentContainerProps = {
  document: IDocument;
  propertyType: PropertyType;
};

const RequiredDocumentContainer = ({ document, propertyType }: TRequiredDocumentContainerProps) => {
  const { t } = useTranslation(['documents', 'common']);

  const { openModal, closeModal, isOpened } = useModal();
  const { value: isLoading, setTrue: startLoading, setFalse: stopLoading } = useBoolean();

  const [formInstance] = useForm();
  const dispatch = useAppDispatch();

  const handleSubmit = useCallback(
    async (values: TRequiredDocumentFormValues) => {
      try {
        startLoading();
        if (document?.id) {
          await dispatch(
            updateDocument({
              property_type: propertyType,
              id: document.id,
              ...values,
            }),
          ).unwrap();
        } else {
          await dispatch(
            createDocument({
              property_type: document.property_type,
              source_type: document.source_type,
              createId: document.createId,
              ...values,
            }),
          ).unwrap();
        }
        showSuccessToast({
          message: t('required_document_saved'),
        });
      } catch {
      } finally {
        stopLoading();
      }
    },
    [propertyType, document, startLoading, stopLoading, dispatch],
  );

  const handleDelete = useCallback(async () => {
    if (!document?.id) {
      showSuccessToast({
        message: t('required_document_deleted'),
      });
      return dispatch(
        removeRequiredDocument({
          id: document.createId ?? '',
          sourceType: document.source_type,
        }),
      );
    }
    try {
      startLoading();
      await dispatch(
        deleteDocument({
          property_type: propertyType,
          id: document.id,
        }),
      ).unwrap();
      closeModal();
      showSuccessToast({
        message: t('required_document_deleted'),
      });
    } catch {
    } finally {
      stopLoading();
    }
  }, [propertyType, document, startLoading, stopLoading, dispatch, closeModal]);

  return (
    <StyledSettingsCard>
      <RequiredDocumentForm onSubmit={handleSubmit} initialValues={document} formInstance={formInstance} />
      <StyledWrapper>
        <Button
          type="primary"
          htmlType="submit"
          shape="round"
          loading={isLoading}
          onClick={formInstance.submit}
          icon={<SaveDocumentIcon />}
        >
          {t('button_save_document')}
        </Button>
        <Button type="primary" danger ghost onClick={openModal} icon={<TrashIcon />} />
      </StyledWrapper>
      <ConfirmationModal
        title={t('document_delete_title')}
        onCancel={closeModal}
        onConfirm={handleDelete}
        isOpened={isOpened}
        okButtonProps={{ danger: true, loading: isLoading }}
      />
    </StyledSettingsCard>
  );
};

export default memo(RequiredDocumentContainer);
